/**
 * ApiTracker — Comprehensive API call debugging system.
 *
 * Wraps fetch() calls with:
 *   - Client-side timing (request start → response received → JSON parsed)
 *   - HTTP status & error capture
 *   - Server-side timing extraction (from response `timestamps` object)
 *   - Slow-call detection (configurable threshold)
 *   - Rolling log with max capacity
 *
 * Usage:
 *   const tracker = new ApiTracker({ slowThreshold: 2000, maxEntries: 50 });
 *   const { data, entry } = await tracker.fetch('/endpoint', { method: 'POST', ... }, 'gamemove');
 */

const STATUS_OK = 'ok';
const STATUS_ERROR = 'error';
const STATUS_SLOW = 'slow';
const STATUS_INFLIGHT = 'inflight';

let _idCounter = 0;

class ApiTracker {
  constructor({ slowThreshold = 2000, maxEntries = 60, onChange = () => {} } = {}) {
    this.slowThreshold = slowThreshold;
    this.maxEntries = maxEntries;
    this.onChange = onChange;
    this.entries = [];
    this._errorCount = 0;
    this._slowCount = 0;
  }

  /** Snapshot of current entries (immutable copy for React state). */
  snapshot() {
    return [...this.entries];
  }

  /** Summary stats. */
  stats() {
    return {
      total: this.entries.length,
      errors: this._errorCount,
      slow: this._slowCount,
      inflight: this.entries.filter(e => e.status === STATUS_INFLIGHT).length,
    };
  }

  /** Clear all entries. */
  clear() {
    this.entries = [];
    this._errorCount = 0;
    this._slowCount = 0;
    this.onChange();
  }

  /**
   * Traced fetch. Returns { data, entry }.
   * `data` is the parsed JSON response (or null on error).
   * `entry` is the log entry object.
   *
   * @param {string} url - The URL to fetch.
   * @param {RequestInit} opts - Standard fetch options.
   * @param {string} label - Human-readable label for this call (e.g. 'gamemove', 'currentgame').
   * @param {object} [extra] - Extra metadata to attach to the log entry.
   */
  async trackedFetch(url, opts = {}, label = '', extra = {}) {
    const id = ++_idCounter;
    const entry = {
      id,
      label: label || _extractLabel(url),
      url,
      method: (opts.method || 'GET').toUpperCase(),
      status: STATUS_INFLIGHT,
      httpStatus: null,
      clientTiming: { start: Date.now(), end: null, parseEnd: null, durationMs: null },
      serverTiming: null,
      error: null,
      slow: false,
      extra,
    };

    this._push(entry);
    this.onChange();

    try {
      const resp = await fetch(url, opts);
      entry.clientTiming.end = Date.now();
      entry.httpStatus = resp.status;

      if (!resp.ok) {
        entry.status = STATUS_ERROR;
        entry.error = `HTTP ${resp.status} ${resp.statusText}`;
        this._errorCount++;
        this.onChange();
        return { data: null, entry, response: resp };
      }

      let data = null;
      const contentType = resp.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        data = await resp.json();
        entry.clientTiming.parseEnd = Date.now();
      } else {
        // Try to parse as text, some endpoints return plain text
        data = await resp.text();
        entry.clientTiming.parseEnd = Date.now();
      }

      entry.clientTiming.durationMs = (entry.clientTiming.parseEnd || entry.clientTiming.end) - entry.clientTiming.start;

      // Extract server-side timestamps if present
      if (data && typeof data === 'object') {
        if (data.timestamps) {
          entry.serverTiming = data.timestamps;
        } else if (data.o && typeof data.o === 'object' && data.o.timestamps) {
          entry.serverTiming = data.o.timestamps;
        }
      }

      // Determine status
      if (entry.clientTiming.durationMs >= this.slowThreshold) {
        entry.status = STATUS_SLOW;
        entry.slow = true;
        this._slowCount++;
      } else {
        entry.status = STATUS_OK;
      }

      this.onChange();
      return { data, entry, response: resp };
    } catch (err) {
      entry.clientTiming.end = Date.now();
      entry.clientTiming.durationMs = entry.clientTiming.end - entry.clientTiming.start;
      entry.status = STATUS_ERROR;
      entry.error = err.message || 'Network error';
      this._errorCount++;
      this.onChange();
      return { data: null, entry, response: null };
    }
  }

  _push(entry) {
    this.entries.unshift(entry);
    if (this.entries.length > this.maxEntries) {
      const removed = this.entries.pop();
      if (removed.status === STATUS_ERROR) this._errorCount = Math.max(0, this._errorCount - 1);
      if (removed.slow) this._slowCount = Math.max(0, this._slowCount - 1);
    }
  }
}

function _extractLabel(url) {
  try {
    const path = new URL(url, window.location.origin).pathname;
    return path.split('/').filter(Boolean).pop() || path;
  } catch {
    return url;
  }
}

export { ApiTracker, STATUS_OK, STATUS_ERROR, STATUS_SLOW, STATUS_INFLIGHT };
